/**
 * Límite mensual de complementos del plan gratuito (spec docs/superpowers/specs/2026-10-17-freemium-complementos-nomina-
 * design.md §5), lógica pura sin DOM: el indicador de cada complemento en el menú «Complementos» de Crear CFDI, si al
 * elegirlo se abre la invitación a Premium en lugar de su formulario, y qué abre FD.upsell con el 402 de límite que
 * responde v2/cfdi/timbrar_ajax (Complementos_limite::respuesta_limite). app.js lo expone como window.FDLimites (un módulo
 * no se importa desde dos bundles): complements.js y timbrado.js (bundle cfdi-crear) lo leen de ahí. El estado de cada
 * clave es el de Complementos_limite::estado_menu: {ilimitado, limite, usados, restante, reinicio}. La nómina no tiene
 * límite desde el 22-oct-2026 (todas las cuentas, solo con timbres): no es una de sus claves.
 *
 * Sus constantes espejan a las del servidor y limite-complementos.test.js las compara con el PHP (Limite::COMPLEMENTOS,
 * Complementos_limite::ORIGEN_UPSELL): quien agregue una clave allá la agrega aquí.
 */

/** El origen medible del modal de Premium (Complementos_limite::ORIGEN_UPSELL). */
export var ORIGEN = 'complementos';

/** Las claves de Limite::COMPLEMENTOS, en su orden. */
export var CLAVES = ['cp', 'comext', 'implocal', 'construccion', 'ine', 'especie', 'vehiculousado', 'donataria', 'hidrocarburo'];

function esClave(c) { return CLAVES.indexOf(String(c)) !== -1; }

/** El estado de una clave como lo manda el servidor, o null si no sirve. */
export function normalizarEstado(e) {
  if (!e || typeof e !== 'object') return null;
  var limite = Number(e.limite);
  var restante = Number(e.restante);
  if (!isFinite(limite) || !isFinite(restante)) return null;
  return {
    ilimitado: !!e.ilimitado,
    limite: Math.max(0, Math.floor(limite)),
    restante: Math.max(0, Math.floor(restante)),
    reinicio: String(e.reinicio || '')
  };
}

/** data-fd-limites del menú (JSON {clave: estado}) → {clave: estado normalizado}; vacío, [] o basura → {}. */
export function leerEstados(texto) {
  var crudo;
  try { crudo = JSON.parse(String(texto || '')); } catch (e) { return {}; }
  var out = {};
  if (!crudo || typeof crudo !== 'object') return out;
  CLAVES.forEach(function (c) {
    var n = normalizarEstado(crudo[c]);
    if (n) out[c] = n;
  });
  return out;
}

/** «2 este mes», «Te queda 1», «Se reanuda el 1 de noviembre»; '' con Premium, módulo suelto o sin estado. */
export function textoIndicador(estado) {
  var e = normalizarEstado(estado);
  if (!e || e.ilimitado) return '';
  if (e.restante <= 0) return e.reinicio ? 'Se reanuda el ' + e.reinicio : 'Se reanuda el mes próximo';
  if (e.restante >= e.limite) return e.limite + ' este mes';
  return (e.restante === 1 ? 'Te queda ' : 'Te quedan ') + e.restante;
}

export function estaAgotado(estado) {
  var e = normalizarEstado(estado);
  return !!e && !e.ilimitado && e.restante <= 0;
}

/**
 * Lo que abre FD.upsell al elegir un complemento que ya llegó a su límite; null si su formulario se abre como siempre.
 * `yaEnLaFactura`: el complemento ya está capturado en esta factura (p. ej. tras el 402 al timbrar). Entonces se abre su
 * formulario, para corregirlo o quitarlo sin recargar; el servidor sigue frenando al timbrar.
 */
export function upsellDeComplemento(clave, estado, yaEnLaFactura) {
  if (yaEnLaFactura || !esClave(clave) || !estaAgotado(estado)) return null;
  var e = normalizarEstado(estado);
  return { origen: ORIGEN, gancho: 'complemento-' + clave, ctx: { n: e.limite, fecha: e.reinicio } };
}

/** El 402 de límite (timbrar_ajax): {origen, gancho, ctx} para FD.upsell, o null si la respuesta no lo es. */
export function upsellDeLimite(r) {
  if (!r || String(r.code) !== '402' || !r.upsell) return null;
  var m = /^complemento-([a-z]+)$/.exec(String(r.gancho || ''));
  if (!m || !esClave(m[1])) return null;
  return { origen: ORIGEN, gancho: 'complemento-' + m[1], ctx: { n: Number(r.limite) || 2, fecha: String(r.reinicio || '') } };
}

/** Tras un 402 de límite, esa clave queda en su límite (el menú lo refleja sin recargar); las demás, igual. */
export function estadosTrasLimite(estados, r) {
  var up = upsellDeLimite(r);
  if (!up) return estados || {};
  var clave = up.gancho.slice('complemento-'.length);
  var out = Object.assign({}, estados || {});
  out[clave] = { ilimitado: false, limite: up.ctx.n, restante: 0, reinicio: up.ctx.fecha };
  return out;
}
